import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiMessageSquare
} from 'react-icons/fi';
import { FaWhatsapp, FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import productsData from '../data/products.json';

const contactInfo = [
  {
    icon: <FiPhone size={22} />,
    label: 'Phone',
    value: '+91 98765 43210\n+91 98765 43211',
    color: '#0F4C81', bg: '#e8f1fb',
    action: 'tel:+919876543210',
  },
  {
    icon: <FiMail size={22} />,
    label: 'Email',
    value: 'info@avivanenterprises.com\nsales@avivanenterprises.com',
    color: '#28A745', bg: '#d4edda',
    action: 'mailto:info@avivanenterprises.com',
  },
  {
    icon: <FiMapPin size={22} />,
    label: 'Office Address',
    value: 'Avivan Enterprises\nTamil Nadu, India',
    color: '#F39C12', bg: '#fef5e7',
    action: null,
  },
  {
    icon: <FiClock size={22} />,
    label: 'Business Hours',
    value: 'Mon–Sat: 9:00 AM – 6:00 PM\nSunday: Closed',
    color: '#9b59b6', bg: '#f5eef8',
    action: null,
  },
];

const businessTypes = [
  'Retailer', 'Wholesaler', 'Supermarket / Modern Trade',
  'Hotel', 'Restaurant', 'Caterer', 'Marriage Hall',
  'Institution / Canteen', 'Online / E-Commerce Seller',
  'Export Buyer', 'Other',
];

const initialForm = {
  name: '', company: '', mobile: '', email: '',
  iam: '', product: '', subject: '', message: '',
};

export default function Contact() {
  const { categories } = productsData;
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1500);
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Avivan Enterprises — FMCG Distribution Partner India</title>
        <meta name="description" content="Get in touch with Avivan Enterprises for product enquiries, dealer partnerships, distribution queries, and business collaboration across Tamil Nadu." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              <FiMessageSquare size={12} /> Get In Touch
            </div>
            <h1>Contact Us</h1>
            <p>Have questions? Our team typically responds within 24 hours.</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>Contact</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="row g-4 mb-5">
            {contactInfo.map((c, i) => (
              <div className="col-md-6 col-lg-3" key={c.label} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="contact-info-card">
                  <div className="cic-icon" style={{ background: c.bg }}>
                    <span style={{ color: c.color }}>{c.icon}</span>
                  </div>
                  <div>
                    <div className="cic-label">{c.label}</div>
                    <div className="cic-value">
                      {c.action ? (
                        <a href={c.action} style={{ color: 'var(--dark)', textDecoration: 'none' }}>
                          {c.value.split('\n').map((line, li) => (
                            <React.Fragment key={li}>{line}{li < c.value.split('\n').length - 1 && <br />}</React.Fragment>
                          ))}
                        </a>
                      ) : (
                        c.value.split('\n').map((line, li) => (
                          <React.Fragment key={li}>{line}{li < c.value.split('\n').length - 1 && <br />}</React.Fragment>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Contact Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 60, justifyContent: 'center' }} data-aos="fade-up">
            <a href="tel:+919876543210" className="btn-primary-c">
              <FiPhone size={16} /> Call Now
            </a>
            <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn-green-c">
              <FaWhatsapp size={18} /> WhatsApp Us
            </a>
            <a href="mailto:info@avivanenterprises.com" className="btn-outline-c">
              <FiMail size={15} /> Send Email
            </a>
          </div>

          {/* Form + Sidebar */}
          <div className="row g-5">
            {/* Enquiry Form */}
            <div className="col-lg-7" data-aos="fade-right">
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 20, padding: '40px', boxShadow: 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: 6 }}>Send Us a Message</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 28 }}>
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                {submitted ? (
                  <div style={{ background: '#d4edda', border: '1px solid #28A745', borderRadius: 12, padding: '32px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
                    <h4 style={{ color: '#1e7e34', fontWeight: 800, marginBottom: 8 }}>Message Sent Successfully!</h4>
                    <p style={{ color: '#155724', margin: '0 0 16px', fontSize: '0.9rem', lineHeight: 1.6 }}>
                      Thank you for contacting us. Our team will respond within <strong>24 business hours</strong>.
                    </p>
                    <button className="btn-primary-c" onClick={() => { setForm(initialForm); setSubmitted(false); }} style={{ fontSize: '0.88rem' }}>
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Full Name *</label>
                          <input className="form-control-c" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Company Name</label>
                          <input className="form-control-c" name="company" placeholder="Business / Company name" value={form.company} onChange={handleChange} />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Mobile Number *</label>
                          <input className="form-control-c" name="mobile" placeholder="10-digit number" value={form.mobile} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Email Address *</label>
                          <input className="form-control-c" name="email" type="email" placeholder="your@email.com" value={form.email} onChange={handleChange} required />
                        </div>
                      </div>

                      {/* I am a... */}
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">I am a...</label>
                          <select className="form-select-c" name="iam" value={form.iam} onChange={handleChange}>
                            <option value="">Select your business type</option>
                            {businessTypes.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Product of Interest */}
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Product of Interest</label>
                          <select className="form-select-c" name="product" value={form.product} onChange={handleChange}>
                            <option value="">Select a category</option>
                            {categories.map(c => (
                              <option key={c.id} value={c.name}>{c.emoji} {c.name}</option>
                            ))}
                            <option value="Multiple Categories">All / Multiple Categories</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-group-c">
                          <label className="form-label-c">Subject</label>
                          <select className="form-select-c" name="subject" value={form.subject} onChange={handleChange}>
                            <option value="">Select a subject</option>
                            {[
                              'Product Enquiry', 'Dealer Partnership', 'Distribution Query',
                              'Pricing & MOQ', 'Careers', 'General Enquiry', 'Other',
                            ].map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="form-group-c">
                          <label className="form-label-c">Message *</label>
                          <textarea
                            className="form-control-c"
                            name="message"
                            rows="5"
                            placeholder="How can we help you? Please describe your query in detail..."
                            value={form.message}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-privacy-note">
                      ℹ️ By submitting you agree to be contacted by our team. No spam, ever.
                    </div>

                    <button type="submit" className="btn-primary-c w-100" style={{ fontSize: '1rem', padding: 15, justifyContent: 'center' }} disabled={submitting}>
                      {submitting
                        ? <><span className="spinner-border spinner-border-sm me-2" /> Sending...</>
                        : <><FiSend size={16} /> Send Message</>}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-lg-5" data-aos="fade-left">
              {/* Map Placeholder */}
              <div className="map-placeholder" style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '3rem' }}>📍</div>
                <div style={{ fontWeight: 700, color: 'var(--dark)' }}>Avivan Enterprises</div>
                <div style={{ fontSize: '0.85rem' }}>Tamil Nadu, India</div>
                <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="btn-primary-c" style={{ marginTop: 12, fontSize: '0.82rem', padding: '8px 20px' }}>
                  Open in Maps
                </a>
              </div>

              {/* Business Hours */}
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 18 }}>
                <h4 style={{ fontWeight: 800, fontSize: '0.96rem', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiClock style={{ color: 'var(--primary)' }} /> Business Hours
                </h4>
                {[
                  { day: 'Monday – Friday', time: '9:00 AM – 6:00 PM', open: true },
                  { day: 'Saturday', time: '9:00 AM – 4:00 PM', open: true },
                  { day: 'Sunday', time: 'Closed', open: false },
                ].map(h => (
                  <div key={h.day} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: '0.86rem' }}>
                    <span style={{ color: 'var(--dark)', fontWeight: 500 }}>{h.day}</span>
                    <span style={{ color: h.open ? 'var(--secondary)' : 'var(--text-muted)', fontWeight: 700 }}>{h.time}</span>
                  </div>
                ))}
              </div>

              {/* Certifications */}
              <div style={{ background: 'var(--light)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 18 }}>
                <h4 style={{ fontWeight: 800, fontSize: '0.96rem', marginBottom: 14 }}>🏅 Our Certifications</h4>
                {[
                  { label: 'FSSAI License', value: 'Food Safety & Standards Authority of India', icon: '✅' },
                  { label: 'GST Registered', value: 'Goods & Services Tax Compliant Business', icon: '📋' },
                  { label: 'Authorized Distributor', value: 'Aachi Masala — Official Channel Partner', icon: '🌶️' },
                ].map(cert => (
                  <div key={cert.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{cert.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--dark)' }}>{cert.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{cert.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div style={{ background: 'var(--gradient-primary)', borderRadius: 16, padding: 24 }}>
                <h4 style={{ color: '#fff', fontWeight: 800, fontSize: '0.96rem', marginBottom: 14 }}>Connect With Us</h4>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  {[
                    { icon: <FaFacebookF />, href: '#', color: '#1877f2' },
                    { icon: <FaInstagram />, href: '#', color: '#e4405f' },
                    { icon: <FaLinkedinIn />, href: '#', color: '#0a66c2' },
                    { icon: <FaWhatsapp />, href: 'https://wa.me/919876543210', color: '#25D366' },
                  ].map((s, i) => (
                    <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                      style={{ width: 42, height: 42, background: 'rgba(255,255,255,0.12)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.1rem', transition: 'var(--transition)', textDecoration: 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.background = s.color; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}>
                      {s.icon}
                    </a>
                  ))}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', margin: 0 }}>
                  Follow us for latest updates on products, dealer opportunities, and company news.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }} data-aos="fade-up">
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, marginBottom: 14 }}>
              Let's Grow <span style={{ color: 'var(--primary)' }}>Together</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.75 }}>
              Whether you're a retailer, wholesaler, institution, or investor — Avivan Enterprises has a partnership model designed for your success.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/become-dealer" className="btn-primary-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                Become a Dealer
              </Link>
              <a href="tel:+919876543210" className="btn-outline-c" style={{ fontSize: '1rem', padding: '12px 34px' }}>
                <FiPhone size={16} /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
